import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import PageHeader from "@/components/PageHeader";
import { CRMInsightsPanel } from "@/components/crm/CRMInsightsPanel";
import { CRMEpisodesList } from "@/components/memory/CRMEpisodesList";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { fetchEventDetail, type CRMEventRecord } from "@/api/events/details";
import { usePageBreadcrumbs } from "@/providers/BreadcrumbProvider";

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<CRMEventRecord | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "episodes">("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    fetchEventDetail(eventId)
      .then((payload) => {
        if (!cancelled) setEvent(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load event.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Contacts", href: "/contacts" },
      { label: "Events", href: "/calendar" },
      { label: event?.title ?? `Event ${eventId ?? ""}` },
    ],
    [event?.title, eventId],
  );
  usePageBreadcrumbs(breadcrumbs);

  return (
    <div className="aa-page-shell space-y-6">
      <PageHeader
        title={event?.title ?? "Event details"}
        description="Autopilot workflow coverage, readiness, and latest episodes."
      />
      {error && <p className="text-xs text-rose-400">{error}</p>}
      <ResizablePanelGroup direction="horizontal" className="aa-glass-card min-h-[400px] rounded-2xl">
        <ResizablePanel defaultSize={65}>
          <div className="p-4">
            <div className="mb-4 inline-flex rounded-full border border-border/60 bg-background/50 p-1 text-xs">
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className={`rounded-full px-3 py-1 ${activeTab === "overview" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("episodes")}
                className={`rounded-full px-3 py-1 ${activeTab === "episodes" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                Episodes
              </button>
            </div>
            {activeTab === "overview" ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Event summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid gap-3 text-sm md:grid-cols-2">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
                        <dd className="text-foreground">{event?.status ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Venue</dt>
                        <dd className="text-foreground">
                          {event?.venue ?? "—"} ({event?.city ?? "—"}, {event?.state ?? event?.country ?? "—"})
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Dates</dt>
                        <dd className="text-foreground">
                          {event?.start_date ?? "—"} → {event?.end_date ?? "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Workflow</dt>
                        <dd className="text-foreground">{event?.workflow_id ?? "No workflow"}</dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 text-sm md:grid-cols-2">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Projected revenue</dt>
                        <dd className="text-emerald-200">
                          {typeof event?.revenue_projection === "number"
                            ? `$${event.revenue_projection.toLocaleString()}`
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">Projected cost</dt>
                        <dd className="text-rose-200">
                          {typeof event?.cost_projection === "number"
                            ? `$${event.cost_projection.toLocaleString()}`
                            : "—"}
                        </dd>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {event?.metadata && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Metadata</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CodeBlock value={JSON.stringify(event.metadata, null, 2)} language="json" />
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Event episodes</CardTitle>
                </CardHeader>
                <CardContent>
                  <CRMEpisodesList entityType="event" entityId={eventId} />
                </CardContent>
              </Card>
            )}
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={35} className="border-l border-border/40">
          <CRMInsightsPanel objectType="event" objectId={eventId} objectName={event?.title ?? undefined} />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
